import { isScheduledPost, comparePostsByDate } from '../../utils/posts';

const updateClassRoomPosts = ({ classRoom, post, posts }) => {
  const updatedClassRoom = { ...classRoom };
  const oldPost = posts[post.objectId];
  const wasScheduled = !!oldPost && isScheduledPost(oldPost);
  const wasImportant = !!oldPost && oldPost.important;
  const scheduled = isScheduledPost(post);
  const listToAdd = scheduled ? 'scheduledPosts' : 'posts';
  const listToRemove = scheduled ? 'posts' : 'scheduledPosts';
  const order = scheduled ? 1 : -1;

  updatedClassRoom[listToAdd] = [
    ...new Set([post.objectId, ...updatedClassRoom[listToAdd]]),
  ]
    .map((id) => (id === post.objectId ? post : posts[id]))
    .sort(comparePostsByDate(order))
    .map((p) => p.objectId);

  updatedClassRoom[listToRemove] = updatedClassRoom[listToRemove].filter(
    (id) => id !== post.objectId
  );

  if (wasScheduled && !scheduled) {
    updatedClassRoom.scheduledPostCount -= 1;
  } else if (!wasScheduled && scheduled) {
    updatedClassRoom.scheduledPostCount += 1;
  }

  if (wasImportant && !post.important) {
    updatedClassRoom.importantPostCount -= 1;
  } else if (!wasImportant && post.important) {
    updatedClassRoom.importantPostCount += 1;
  }

  return updatedClassRoom;
};

export const createdPostReducer = (state, action) => {
  switch (action.status) {
    case 'success': {
      const post = {
        ...action.post,
        comments: [],
        tags: action.post.tags || [],
      };
      const classRoom = state.classRooms[post.classRoom];

      return {
        ...state,
        classRooms: {
          ...state.classRooms,
          [post.classRoom]: updateClassRoomPosts({
            classRoom,
            post,
            posts: state.posts,
          }),
        },
        posts: {
          ...state.posts,
          [post.objectId]: post,
        },
      };
    }
    default:
      return state;
  }
};

export const editPostReducer = (state, action) => {
  if (action.status !== 'success') {
    return state;
  }

  const { post } = action;
  const classRoom = state.classRooms[post.classRoom];

  return {
    ...state,
    classRooms: {
      ...state.classRooms,
      [post.classRoom]: updateClassRoomPosts({
        classRoom,
        post,
        posts: state.posts,
      }),
    },
    posts: {
      ...state.posts,
      [action.post.objectId]: {
        ...state.posts[action.post.objectId],
        ...action.post,
      },
    },
  };
};

export const deletedPostReducer = (state, action) => {
  switch (action.status) {
    case 'started':
      return {
        ...state,
        isUpdatingPost: true,
      };
    case 'success': {
      const classRoom = state.classRooms[action.post.classRoom];
      const updatedClassRoom = { ...classRoom };
      const posts = { ...state.posts };
      const { post } = action;
      const postId = post.objectId;
      const wasScheduled = isScheduledPost(post);

      delete posts[post.objectId];

      const differentId = (id) => id !== postId;
      updatedClassRoom.posts = updatedClassRoom.posts.filter(differentId);
      updatedClassRoom.scheduledPosts = updatedClassRoom.scheduledPosts.filter(
        differentId
      );
      const fewerScheduled =
        updatedClassRoom.scheduledPosts.length <
        classRoom.scheduledPosts.length;
      if (wasScheduled && fewerScheduled) {
        updatedClassRoom.scheduledPostCount -= 1;
      }

      return {
        ...state,
        classRooms: {
          ...state.classRooms,
          [classRoom.objectId]: updatedClassRoom,
        },
        posts,
        isUpdatingPost: false,
      };
    }
    case 'error':
      return {
        ...state,
        isUpdatingPost: false,
      };
    default:
      return {
        ...state,
        isUpdatingPost: false,
      };
  }
};

export const fetchClassRoomPostReducer = (state, action) => {
  switch (action.status) {
    case 'started':
      return {
        ...state,
        isFetchingData: true,
      };
    case 'success':
      return {
        ...state,
        posts: {
          ...state.posts,
          [action.post.objectId]: {
            comments: [],
            tags: [],
            user: action.post.user,
            ...state.posts[action.post.objectId],
            ...action.post,
          },
        },
        isFetchingData: false,
      };
    case 'error':
      return {
        ...state,
        isFetchingData: false,
      };

    default:
      return state;
  }
};

export const fetchClassRoomContentReducer = (state, action) => {
  switch (action.status) {
    case 'started':
      if (action.reset) {
        const classRooms = { ...state.classRooms };
        classRooms[action.spaceId] = Object.assign(
          {},
          classRooms[action.spaceId],
          {
            posts: [],
          }
        );
        return {
          ...state,
          classRooms,
        };
      }
      return {
        ...state,
      };
    case 'success': {
      const posts = Object.assign({}, action.reset ? [] : state.posts);
      for (let i = 0; i < action.content.length; i += 1) {
        posts[action.content[i].objectId] = Object.assign(
          {},
          posts[action.content[i].objectId],
          action.content[i],
          {
            classRoom: action.content[i].classRoom,
            comments: [],
            tags: [],
            user: action.content[i].user || {
              firstName: 'Deleted User',
              lastName: '',
            },
          }
        );
      }
      const classRooms = { ...state.classRooms };
      if (action.content.length > 0) {
        const classRoomId = action.content[0].classRoom;
        const actionPosts = action.content.map((post) => post.objectId);
        const oldClassRoomPosts = classRooms[classRoomId].posts;
        const uniquePostIds = [
          ...new Set([...actionPosts, ...oldClassRoomPosts]),
        ];
        const comparePostOrder = -1;
        const updatedClassRoomPosts = uniquePostIds
          .map((id) => posts[id])
          .sort(comparePostsByDate(comparePostOrder))
          .map((p) => p.objectId);
        const oldestPostDate = new Date(
          action.content[action.content.length - 1].createdAt
        );
        classRooms[classRoomId] = {
          ...classRooms[classRoomId],
          posts: updatedClassRoomPosts,
          oldestPostDate,
        };
      }
      return {
        ...state,
        posts,
        classRooms,
      };
    }
    case 'error':
      return {
        ...state,
        hasInitialData: false,
        isFetchingInitialData: false,
      };

    default:
      return state;
  }
};
